import type { Breadcrumb, Scope, SeverityLevel } from '@sentry/core';
import { useNuxtApp } from '#app';

interface UseSentryComposable {
  captureException: (error: unknown, context?: Record<string, unknown>) => void;
  captureMessage: (message: string, level?: SeverityLevel, context?: Record<string, unknown>) => void;
  setUser: (user: Record<string, unknown> | null) => void;
  setContext: (name: string, context: Record<string, unknown> | null) => void;
  setTag: (key: string, value: string) => void;
  withScope: (callback: (scope: Scope) => void) => void;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
  sdk?: typeof import('@sentry/vue');
}

const noop = (): void => {};
const noopBreadcrumb: (breadcrumb: Breadcrumb) => void = () => {};

export const useSentry = (): UseSentryComposable => {
  const nuxtApp = useNuxtApp();

  const captureException =
    nuxtApp.$sentryCaptureException ??
    ((error: unknown, context?: Record<string, unknown>): void => {
      console.error('[Sentry disabled] captureException', error, context);
    });

  const captureMessage =
    nuxtApp.$sentryCaptureMessage ??
    ((message: string, level: SeverityLevel = 'info', context?: Record<string, unknown>): void => {
      console.debug(`[Sentry disabled] ${level.toUpperCase()} ${message}`, context);
    });

  return {
    captureException,
    captureMessage,
    setUser: nuxtApp.$sentrySetUser ?? noop,
    setContext: nuxtApp.$sentrySetContext ?? noop,
    setTag: nuxtApp.$sentrySetTag ?? noop,
    withScope: nuxtApp.$sentryScope ?? noop,
    addBreadcrumb: nuxtApp.$sentryAddBreadcrumb ?? noopBreadcrumb,
    sdk: nuxtApp.$sentrySdk,
  };
};
